import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Organization } from '../types';
import { apiService } from '../services/api';
import { storeOrganization, clearStoredOrganization } from '../utils/organizationSession';

interface OrganizationState {
  current: Organization | null;
  loading: boolean;
  error: string | null;
}

const initialState: OrganizationState = {
  current: null,
  loading: false,
  error: null,
};

// Async thunk to fetch organization by token
export const fetchOrganizationByToken = createAsyncThunk(
  'organization/fetchByToken',
  async (token: string, { rejectWithValue }) => {
    try {
      const organization = await apiService.organizations.getByToken(token);
      return organization;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch organization');
    }
  }
);

// Async thunk to fetch organization by organization_id
export const fetchOrganizationById = createAsyncThunk(
  'organization/fetchById',
  async (organizationId: string, { rejectWithValue }) => {
    try {
      const organization = await apiService.organizations.getById(organizationId);
      return organization;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch organization');
    }
  }
);

const organizationSlice = createSlice({
  name: 'organization',
  initialState,
  reducers: {
    clearOrganization: (state) => {
      state.current = null;
      state.error = null;
      // Clear from localStorage when organization is cleared
      clearStoredOrganization();
    },
    setOrganization: (state, action: PayloadAction<Organization>) => {
      state.current = action.payload;
      state.error = null;
      // Persist to localStorage when organization is set
      storeOrganization(action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrganizationByToken.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrganizationByToken.fulfilled, (state, action) => {
        state.loading = false;
        state.current = action.payload;
        state.error = null;
        // Persist to localStorage when fetch succeeds
        storeOrganization(action.payload);
      })
      .addCase(fetchOrganizationByToken.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchOrganizationById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrganizationById.fulfilled, (state, action) => {
        state.loading = false;
        state.current = action.payload;
        state.error = null;
        // Persist to localStorage when fetch succeeds
        storeOrganization(action.payload);
      })
      .addCase(fetchOrganizationById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearOrganization, setOrganization } = organizationSlice.actions;
export default organizationSlice.reducer;